import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api/client.js';

const LIMIT = 10;

const S = {
  top: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  h1: { fontFamily: 'var(--display)', fontSize: 32, fontWeight: 400, margin: 0 },
  addBtn: {
    padding: '10px 22px', background: 'var(--slate)', color: 'var(--bone)', border: 0,
    fontFamily: 'var(--label)', fontSize: 11, letterSpacing: '.24em',
    textTransform: 'uppercase', cursor: 'pointer',
  },
  filters: {
    display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 16, marginBottom: 24,
    padding: '16px 18px', background: 'var(--bone-soft)', border: '1px solid var(--bronze-30)',
  },
  fGroup: { display: 'flex', flexDirection: 'column', gap: 5 },
  fLabel: {
    fontFamily: 'var(--label)', fontSize: 9, letterSpacing: '.2em',
    textTransform: 'uppercase', color: 'var(--slate-70)',
  },
  control: {
    padding: '7px 10px', border: '1px solid var(--bronze-30)', background: 'var(--white)',
    fontFamily: 'var(--body)', fontSize: 14, color: 'var(--slate)', outline: 'none',
  },
  clearBtn: {
    fontFamily: 'var(--label)', fontSize: 10, letterSpacing: '.2em', textTransform: 'uppercase',
    cursor: 'pointer', background: 'none', border: 'none', color: 'var(--bronze)', padding: '8px 0',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    fontFamily: 'var(--label)', fontSize: 10, letterSpacing: '.24em', textTransform: 'uppercase',
    color: 'var(--bronze)', textAlign: 'left', padding: '10px 12px',
    borderBottom: '1px solid rgba(139,115,85,.2)',
  },
  td: {
    fontFamily: 'var(--body)', fontSize: 16, padding: '14px 12px',
    borderBottom: '1px solid rgba(139,115,85,.1)', color: 'var(--slate)',
  },
  action: {
    fontFamily: 'var(--label)', fontSize: 10, letterSpacing: '.2em', textTransform: 'uppercase',
    cursor: 'pointer', background: 'none', border: 'none', color: 'var(--bronze)', marginRight: 12,
  },
  del: { color: '#c44' },
  badge: (on) => ({
    fontFamily: 'var(--label)', fontSize: 9, letterSpacing: '.18em', textTransform: 'uppercase',
    padding: '3px 9px', border: '1px solid',
    color: on ? 'var(--bronze)' : 'var(--slate-70)',
    borderColor: on ? 'var(--bronze)' : 'var(--bronze-30)',
    background: on ? 'rgba(139,115,85,.08)' : 'transparent',
  }),
  pager: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 24 },
  pBtn: (active, disabled) => ({
    fontFamily: 'var(--label)', fontSize: 11, letterSpacing: '.12em', minWidth: 32,
    padding: '6px 10px', cursor: disabled ? 'default' : 'pointer',
    background: active ? 'var(--slate)' : 'var(--white)',
    color: active ? 'var(--bone)' : (disabled ? 'var(--bronze-30)' : 'var(--slate)'),
    border: '1px solid var(--bronze-30)',
  }),
};

export default function MediaList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [lang, setLang] = useState('all');
  const [status, setStatus] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const load = () => {
    api.mediaList({
      lang: lang === 'all' ? undefined : lang,
      status,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      page,
      limit: LIMIT,
    })
      .then(r => {
        setItems(r.data || []);
        setTotalPages(r.meta?.total_pages || 1);
      })
      .catch(() => {});
  };

  useEffect(() => { load(); }, [lang, status, dateFrom, dateTo, page]);

  const togglePublish = async (item) => {
    try {
      await api.updateMedia(item.id, { published: !item.published });
      load();
    } catch (e) { alert(e.message); }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this media item?')) return;
    try {
      await api.deleteMedia(id);
      load();
    } catch (e) { alert(e.message); }
  };

  const resetFilters = () => {
    setLang('all'); setStatus('all'); setDateFrom(''); setDateTo(''); setPage(1);
  };

  return (
    <div>
      <div style={S.top}>
        <h1 style={S.h1}>Media</h1>
        <button style={S.addBtn} onClick={() => navigate('/admin/media/new')}>+ New</button>
      </div>

      <div style={S.filters}>
        <div style={S.fGroup}>
          <label style={S.fLabel}>Language</label>
          <select style={S.control} value={lang} onChange={e => { setLang(e.target.value); setPage(1); }}>
            <option value="all">All</option>
            <option value="cn">中文 (CN)</option>
            <option value="en">English (EN)</option>
          </select>
        </div>
        <div style={S.fGroup}>
          <label style={S.fLabel}>Status</label>
          <select style={S.control} value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
            <option value="all">All</option>
            <option value="published">Published</option>
            <option value="unpublished">Draft</option>
          </select>
        </div>
        <div style={S.fGroup}>
          <label style={S.fLabel}>Created from</label>
          <input type="date" style={S.control} value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }} />
        </div>
        <div style={S.fGroup}>
          <label style={S.fLabel}>Created to</label>
          <input type="date" style={S.control} value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }} />
        </div>
        <button style={S.clearBtn} onClick={resetFilters}>Clear</button>
      </div>

      <table style={S.table}>
        <thead>
          <tr>
            <th style={S.th}>Lang</th>
            <th style={S.th}>Type</th>
            <th style={S.th}>Title</th>
            <th style={S.th}>Date</th>
            <th style={S.th}>Status</th>
            <th style={S.th}></th>
          </tr>
        </thead>
        <tbody>
          {items.map(item => (
            <tr key={item.id}>
              <td style={S.td}>{(item.lang || '').toUpperCase()}</td>
              <td style={S.td}>{item.type === 'article' ? 'Long-form' : 'Video'}</td>
              <td style={S.td}>{item.title || '(no title)'}</td>
              <td style={S.td}>{item.date}</td>
              <td style={S.td}>
                <span style={S.badge(item.published)}>{item.published ? 'Published' : 'Draft'}</span>
              </td>
              <td style={S.td}>
                <button style={S.action} onClick={() => togglePublish(item)}>
                  {item.published ? 'Unpublish' : 'Publish'}
                </button>
                <button style={S.action} onClick={() => navigate(`/admin/media/${item.id}/edit`)}>Edit</button>
                <button style={{ ...S.action, ...S.del }} onClick={() => handleDelete(item.id)}>Delete</button>
              </td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr><td style={S.td} colSpan={6}>No media items match these filters.</td></tr>
          )}
        </tbody>
      </table>

      {totalPages > 1 && (
        <div style={S.pager}>
          <button style={S.pBtn(false, page === 1)} disabled={page === 1} onClick={() => setPage(page - 1)}>Prev</button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
            <button key={n} style={S.pBtn(n === page, false)} onClick={() => setPage(n)}>{n}</button>
          ))}
          <button style={S.pBtn(false, page === totalPages)} disabled={page === totalPages} onClick={() => setPage(page + 1)}>Next</button>
        </div>
      )}
    </div>
  );
}
